import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { areAllFieldsValid, setFieldAndValidate } from '../../../../utils/field-utils';
import { UamRegistrationPanelContainer } from '../../../../styles-and-themes/styles/uam-registration-styles';
import BackAndContinueControlSet from '../../ControlSets/BackAndContinueControlSet/BackAndContinueControlSet';
import FirstAndLastNameControlSet from '../../ControlSets/FirstAndLastNameControlSet/FirstAndLastNameControlSet';
import CountrySelectorControlSet from '../../ControlSets/CountrySelectorControlSet/CountrySelectorControlSet';
import RoleSelectorControlSet from '../../ControlSets/RoleSelectorControlSet/RoleSelectorControlSet';
import SchoolSelectorSet from '../../ControlSets/SchoolSelectorSet/SchoolSelectorSet';
import DisciplineSelectorSet from '../../ControlSets/DisciplineSelectorSet/DisciplineSelectorSet';
import { destinationInfo } from '../../../../destructured-front-end-config';
import { composeDestinationQuerystring } from '../../../../../shared-with-server/destination-utils';
import { userFieldSchemas } from '../../../../utils/validation-utils';
import {
  getLocalStorageRegistrationInfo, multiStepResultTypes, processMultiStepRegistrationEntry, setLocalStorageRegistrationInfo,
} from '../../../../utils/uam-utils';

const Step1RegistrationPanel = (
  {
    brand,
    destPath,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    countryOrRegion,
    setCountryOrRegion,
    role,
    setRole,
    availableRoles,
    roleType,
    setRoleType,
    school,
    setSchool,
    discipline,
    setDiscipline,
    availableDisciplines,
    setEmailPending,
    setRegistrationComplete,
  },
) => {
  const isNewRegistration = useRef(localStorage.getItem('is_new_registration') === 'true');

  const allFieldsValid = areAllFieldsValid(
    [
      firstName,
      lastName,
      countryOrRegion,
      role,
      ...(roleType === 'Student' || roleType === 'Instructor' ? [school] : []),
      ...(roleType === 'Instructor' ? [discipline] : []),
    ],
  );

  const onContinue = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!allFieldsValid) {
      return; // should never happen as button should be disabled until all fields are valid, but just in case
    }

    // TODO: Indicate busy state

    axios.patch(
      `/api/v2/fi/multi-step-registration?${composeDestinationQuerystring(destinationInfo, destPath)}`,
      {
        firstName: firstName.value,
        lastName: lastName.value,
        country: countryOrRegion.value,
        role: role.value,
        institutionId: school.value.institutionId,
        school: school.value.institution,
        cAccount: school.value.cAccount,
        discipline: discipline.value,
      },
      { validateStatus: (status) => status < 500 },
    ).then((registrationResult) => {
      if (registrationResult.status === 200) {
        window.location.assign(`/uam/step2?${composeDestinationQuerystring(destinationInfo, destPath)}`);
      } else if (registrationResult.status === 401) {
        // This scenario can often occur if a user STARTS a registration but then leaves for a while before
        // finishing out the first main step, by which time the registration token has expired.
        // In this case, we need to refresh the registration token and try again.
        axios.post(`/api/v2/fi/multi-step-registration-entry?${composeDestinationQuerystring(destinationInfo, destPath)}`, {
          email: getLocalStorageRegistrationInfo('email'),
        }, { validateStatus: (status) => status < 500 }).then((registrationEntryResult) => {
          const result = processMultiStepRegistrationEntry(registrationEntryResult.status, registrationEntryResult.data, setEmailPending, setRegistrationComplete);

          if (result.status === multiStepResultTypes.error) {
            alert('An unexpected error occurred saving registration data. You will need to start over.');
            console.error('Error saving registration data', registrationEntryResult);
            window.location.assign(`/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`);
          }
        });
      } else {
        alert('An unexpected error occurred saving registration data. You will need to start over.');
        console.error('Error saving registration data', registrationResult);
        window.location.assign(`/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`);
      }
    });
  };

  // empty effect to load data if not new registration
  useEffect(() => {
    if (isNewRegistration.current) {
      return;
    }

    axios.get('/api/v2/fi/multi-step-registration', { validateStatus: (status) => status < 500 || status >= 600 }).then((registrationResult) => {
      if (registrationResult.status === 200) {
        const step1Data = registrationResult.data.step1;

        setFieldAndValidate(firstName, setFirstName, step1Data.firstName, userFieldSchemas.firstName);
        setFieldAndValidate(lastName, setLastName, step1Data.lastName, userFieldSchemas.lastName);
        setFieldAndValidate(countryOrRegion, setCountryOrRegion, step1Data.country, userFieldSchemas.countryOrRegion);
        setFieldAndValidate(role, setRole, step1Data.role, userFieldSchemas.role);
        setFieldAndValidate(school, setSchool, { institution: step1Data.school, institutionId: step1Data.institutionId, cAccount: step1Data.cAccount }, userFieldSchemas.school);
        setFieldAndValidate(discipline, setDiscipline, step1Data.discipline, userFieldSchemas.discipline);
      } else if (registrationResult.status === 401) {
        axios.post(`/api/v2/fi/multi-step-registration-entry?${composeDestinationQuerystring(destinationInfo, destPath)}`, {
          email: getLocalStorageRegistrationInfo('email'),
        }, { validateStatus: (status) => status < 500 }).then((registrationEntryResult) => {
          const result = processMultiStepRegistrationEntry(registrationEntryResult.status, registrationEntryResult.data, setEmailPending, setRegistrationComplete);

          if (result.status === multiStepResultTypes.error) {
            alert('An unexpected error occurred while getting registration data. You will need to start over.');
            console.error('Error saving registration data', registrationEntryResult);
            window.location.assign(`/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`);
          }
        });
      }
    });
  }, []);

  return (
    <form onSubmit={onContinue}>
      <UamRegistrationPanelContainer>
        <FirstAndLastNameControlSet
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
        />

        <CountrySelectorControlSet
          countryOrRegion={countryOrRegion}
          setCountryOrRegion={setCountryOrRegion}
        />

        <RoleSelectorControlSet
          role={role}
          setRole={setRole}
          roleType={roleType}
          setRoleType={setRoleType}
          availableRoles={availableRoles}
        />

        {
          (roleType === 'Student' || roleType === 'Instructor' || roleType === 'Employee') && (
            <SchoolSelectorSet
              school={school}
              setSchool={setSchool}
              brand={brand}
            />
          )
        }

        {
          (roleType === 'Instructor') && (
            <DisciplineSelectorSet
              discipline={discipline}
              setDiscipline={setDiscipline}
              availableDisciplines={availableDisciplines}
            />
          )
        }

        <BackAndContinueControlSet
          allFieldsValid={allFieldsValid}
          onBack={(e) => {
            e.preventDefault();
            e.stopPropagation();

            setLocalStorageRegistrationInfo('step1', {
              firstName: firstName.value,
              lastName: lastName.value,
              country: countryOrRegion.value,
              role: role.value,
              school: school.value.institution,
              institutionId: school.value.institutionId,
              cAccount: school.value.cAccount,
              discipline: role.value === 'Instructor' ? discipline.value : null,
            });

            window.location.assign(`/uam/email?${composeDestinationQuerystring(destinationInfo, destPath)}`);
          }}
          onContinue={onContinue}
        />
      </UamRegistrationPanelContainer>
    </form>
  );
};

Step1RegistrationPanel.propTypes = {
  brand: PropTypes.object.isRequired,
  destPath: PropTypes.string.isRequired,
  firstName: PropTypes.object.isRequired,
  setFirstName: PropTypes.func.isRequired,
  lastName: PropTypes.object.isRequired,
  setLastName: PropTypes.func.isRequired,
  countryOrRegion: PropTypes.object.isRequired,
  setCountryOrRegion: PropTypes.func.isRequired,
  role: PropTypes.object.isRequired,
  setRole: PropTypes.func.isRequired,
  availableRoles: PropTypes.array.isRequired,
  roleType: PropTypes.string.isRequired,
  setRoleType: PropTypes.func.isRequired,
  school: PropTypes.object.isRequired,
  setSchool: PropTypes.func.isRequired,
  discipline: PropTypes.object.isRequired,
  setDiscipline: PropTypes.func.isRequired,
  availableDisciplines: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  setEmailPending: PropTypes.func.isRequired,
  setRegistrationComplete: PropTypes.func.isRequired,
};

export default Step1RegistrationPanel;
